import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Tooltip } from 'components/common';
import { setFilters, setFilter } from 'actions/search';
import { getFilter, getActiveFilters } from 'selectors/search';
import { formatCurrency } from 'utils/number';
import {
  activityCodes,
  applicationTypes,
  instituteCodes,
} from 'services/inputTokens';

import { FilterSection } from './styled';
import { Filter, helpText } from '../helpers';
import { Container } from 'styled';
import FilterGroup from './FilterGroup';
import { fundingKeys } from './keys';
import {
  coreFlagForActivityCode,
  getFundingCheckboxStates,
} from './fundingFilterRules';
import { getResearcherText } from 'utils/constants';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';
import { applyPresetSideEffects } from './presetSideEffects';

const Funding = ({ loading, scrollableContainer }) => {
  const dispatch = useDispatch();

  const nihOnlyKeys = [
    'nihApplicationType',
    'nihActivityCode',
    'nihInstituteCode',
  ];

  const activeFilters = useSelector(getActiveFilters(Object.keys(fundingKeys)));
  const fundingStarts = useSelector(getFilter('fundingStarts'));
  const grantExpiry = useSelector(getFilter(fundingKeys.grantExpiry.key));
  const earlyAwardDate = useSelector(getFilter(fundingKeys.earlyAwardDate.key));

  const {
    grantExpiryLocked,
    earlyAwardDateLocked,
    grantExpiryDisabled,
    earlyAwardDateDisabled,
  } = getFundingCheckboxStates({
    loading,
    grantExpiry,
    earlyAwardDate,
    fundingStarts,
  });

  const handleCore = value => {
    dispatch(setFilter('core', value));
    dispatch(applyPresetSideEffects({ core: value }));
  };

  const handleActivityCodeChange = value => {
    if (value === null) {
      return null;
    }

    dispatch(setFilters({ core: coreFlagForActivityCode(value) }));
    dispatch(setFilter('nihActivityCode', value));
  };

  return (
    <FilterGroup
      icon='dollar'
      title='Funding'
      activeKeys={activeFilters}
      sectionKey='funding'
    >
      <>
        <Filter
          placeholder={['Awarded From', 'Awarded To']}
          type='range'
          filterType='datePicker'
          filterId={fundingKeys.dateAwarded.key}
          disabled={loading}
          closeOnScroll
          closeOnScrollContainer={scrollableContainer}
        />
        <Filter
          filterType='number'
          filterId={fundingKeys.minFunding.key}
          formatter={formatCurrency}
          placeholder='Min. Funding (3 Years)'
          step={10000}
          min={0}
          helpText={`Filter results by the minimum 3 year funding a ${getResearcherText()} has received`}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId={fundingKeys.maxFunding.key}
          formatter={formatCurrency}
          placeholder='Max. Funding (3 Years)'
          step={10000}
          min={0}
          helpText={`Filter results by the maximum 3 year funding a ${getResearcherText()} has received`}
          disabled={loading}
        />

        <Tooltip
          title={
            grantExpiryLocked ? helpText.filterDisabled : helpText.grantExpiry
          }
          placement='right'
          overlayStyle={{ maxWidth: 450 }}
        >
          <Filter
            filterType='checkbox'
            filterId={fundingKeys.grantExpiry.key}
            disabled={grantExpiryDisabled}
            labelText='Funding Expiring Within 3 Months'
          />
        </Tooltip>
        {/*     <Filter
        filterType='token'
        placeholder='Funding Round'
        filterId='fundingRound'
        options={fundingRounds}
      />*/}
        <FilterSection
          showInactive={activeFilters.some(key => nihOnlyKeys.includes(key))}
        >
          NIH Only
        </FilterSection>
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Application Type'
          filterId={fundingKeys.nihApplicationType.key}
          options={applicationTypes}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Activity Code'
          filterId={fundingKeys.nihActivityCode.key}
          options={activityCodes}
          onChange={handleActivityCodeChange}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Institute Code'
          filterId={fundingKeys.nihInstituteCode.key}
          options={instituteCodes}
          disabled={loading}
        />
        <Container>
          <Filter
            filterType='checkbox'
            helpText={
              earlyAwardDateLocked
                ? helpText.filterDisabled
                : helpText.earlyAwardDate
            }
            filterId={fundingKeys.earlyAwardDate.key}
            disabled={earlyAwardDateDisabled}
            labelText='Early Budget'
          />
          <Filter
            filterType='switch'
            filterId={fundingKeys.core.key}
            onChange={handleCore}
            disabled={loading}
          >
            Core Labs Only
          </Filter>
        </Container>
      </>
    </FilterGroup>
  );
};

Funding.propTypes = {
  loading: PropTypes.bool,
};

export default Funding;
